import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Card, 
  Typography, 
  Table, 
  Button, 
  Tag, 
  Space, 
  Input, 
  Row, 
  Col, 
  message, 
  Popconfirm,
  Progress,
  Tooltip,
  Statistic,
  DatePicker,
  Select
} from 'antd';
import { 
  EyeOutlined, 
  ReloadOutlined,
  SearchOutlined,
  ClearOutlined,
  ApiOutlined,
  ClockCircleOutlined,
  UserOutlined,
  CalendarOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import {
  getVisitorConfig,  // Add this import
  getVisitorsList,   // Use Redux action instead of fetch
  resetVisitorLimits,
  clearMessages
} from '../../features/visitor/visitorLimitsSlice';

const { Title, Text } = Typography;
const { Search } = Input;
const { RangePicker } = DatePicker;

const VisitorList = () => {
  const dispatch = useDispatch();
  const { 
    config, 
    visitors, 
    visitorsTotal,
    visitorsLoading, 
    loading, 
    error, 
    success 
  } = useSelector((state) => state.visitorLimits);
  
  const [filteredVisitors, setFilteredVisitors] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  useEffect(() => {
    // Load both config and visitors list
    dispatch(getVisitorConfig());
    dispatch(getVisitorsList());
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      message.success(success);
      dispatch(clearMessages());
      dispatch(getVisitorsList()); // Refresh the list
    }
  }, [success, dispatch]);

  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearMessages());
    }
  }, [error, dispatch]);

  useEffect(() => {
    filterVisitors();
  }, [visitors, searchText, statusFilter, dateRange]);

  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      total: visitorsTotal
    }));
  }, [visitorsTotal]);

  const loadVisitors = () => {
    dispatch(getVisitorsList());
  };

  const filterVisitors = () => {
    let filtered = [...visitors];

    // Search filter
    if (searchText) {
      filtered = filtered.filter(visitor => 
        visitor.name?.toLowerCase().includes(searchText.toLowerCase()) ||
        visitor.email?.toLowerCase().includes(searchText.toLowerCase()) ||
        visitor._id?.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(visitor => {
        const percentage = (visitor.requestCount / config.visitorRequestLimit) * 100;
        switch (statusFilter) {
          case 'normal':
            return percentage < 75;
          case 'warning':
            return percentage >= 75 && percentage < 90;
          case 'critical':
            return percentage >= 90 && percentage < 100;
          case 'exceeded':
            return percentage >= 100;
          default:
            return true;
        }
      });
    }

    // Date range filter
    if (dateRange && dateRange.length === 2) {
      const [start, end] = dateRange;
      filtered = filtered.filter(visitor => {
        const lastActivity = new Date(visitor.lastActivity);
        return lastActivity >= start.toDate() && lastActivity <= end.toDate();
      });
    }

    setFilteredVisitors(filtered);
  };

  const handleResetLimits = async (userId) => {
    try {
      await dispatch(resetVisitorLimits(userId)).unwrap();
    } catch (error) {
      // Error handled by useEffect
    }
  };

  const getUsageColor = (current, limit) => {
    const percentage = (current / limit) * 100;
    if (percentage >= 100) return '#ff4d4f';
    if (percentage >= 90) return '#fa8c16';
    if (percentage >= 75) return '#faad14';
    return '#52c41a';
  };

  const getUsageStatus = (current, limit) => {
    const percentage = (current / limit) * 100;
    if (percentage >= 100) return { text: 'Exceeded', color: 'red' };
    if (percentage >= 90) return { text: 'Critical', color: 'orange' };
    if (percentage >= 75) return { text: 'Warning', color: 'gold' };
    return { text: 'Normal', color: 'green' };
  };

  const columns = [
    {
      title: 'Visitor',
      dataIndex: 'name',
      key: 'visitor',
      render: (text, record) => (
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
            <EyeOutlined className="text-purple-600" />
          </div>
          <div>
            <div className="font-medium">{text || 'Anonymous'}</div>
            <div className="text-sm text-gray-500">{record.email || record._id.substring(0, 8)}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Usage',
      key: 'usage',
      render: (_, record) => {
        const percentage = Math.round((record.requestCount / config.visitorRequestLimit) * 100);
        const status = getUsageStatus(record.requestCount, config.visitorRequestLimit);
        
        return (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Text className="text-sm">{record.requestCount} / {config.visitorRequestLimit}</Text>
              <Tag color={status.color}>{status.text}</Tag>
            </div>
            <Progress
              percent={percentage}
              strokeColor={getUsageColor(record.requestCount, config.visitorRequestLimit)}
              size="small"
              showInfo={false}
            />
          </div>
        );
      },
    },
    {
      title: 'Remaining',
      key: 'remaining',
      render: (_, record) => {
        const remaining = Math.max(0, config.visitorRequestLimit - record.requestCount);
        return (
          <Statistic
            value={remaining}
            suffix="requests"
            valueStyle={{ 
              fontSize: '14px',
              color: remaining === 0 ? '#ff4d4f' : '#52c41a'
            }}
          />
        );
      },
    },
    {
      title: 'Last Activity',
      dataIndex: 'lastActivity',
      key: 'lastActivity',
      render: (date) => {
        if (!date) return <Text type="secondary">Never</Text>;
        const activityDate = new Date(date);
        const now = new Date();
        const diffInHours = Math.round((now - activityDate) / (1000 * 60 * 60));
        
        return (
          <Tooltip title={activityDate.toLocaleString()}>
            <div className="flex items-center space-x-1">
              <ClockCircleOutlined className="text-gray-400" />
              <Text className="text-sm">
                {diffInHours < 1 ? 'Just now' : 
                 diffInHours < 24 ? `${diffInHours}h ago` : 
                 `${Math.round(diffInHours / 24)}d ago`}
              </Text>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Next Reset',
      key: 'nextReset',
      render: (_, record) => {
        const nextReset = record.nextReset ? new Date(record.nextReset) : 
                         new Date(Date.now() + 24 * 60 * 60 * 1000);
        const now = new Date();
        const hoursUntilReset = Math.round((nextReset - now) / (1000 * 60 * 60));
        
        return (
          <Tooltip title={nextReset.toLocaleString()}>
            <div className="flex items-center space-x-1">
              <CalendarOutlined className="text-gray-400" />
              <Text className="text-sm">
                {hoursUntilReset < 1 ? 'Soon' : `${hoursUntilReset}h`}
              </Text>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const isExceeded = record.requestCount >= config.visitorRequestLimit;
        
        return (
          <Space>
            <Popconfirm
              title="Reset Visitor Limits"
              description="Are you sure you want to reset this visitor's request limits? This will allow them to make requests again."
              onConfirm={() => handleResetLimits(record._id)}
              okText="Yes, Reset"
              cancelText="Cancel"
              icon={<ExclamationCircleOutlined style={{ color: 'orange' }} />}
            >
              <Button 
                size="small" 
                icon={<ClearOutlined />} 
                type={isExceeded ? "primary" : "default"}
                danger={isExceeded}
                loading={loading}
                disabled={record.requestCount === 0}
              >
                Reset
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="shadow-soft border border-border-light rounded-xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-600 rounded-full flex items-center justify-center">
              <UserOutlined className="text-xl text-white" />
            </div>
            <div>
              <Title level={3} className="mb-1">Visitor Activity</Title>
              <Text className="text-gray-600">Monitor visitor request usage and limits</Text>
            </div>
          </div>
          <Button 
            icon={<ReloadOutlined />}
            onClick={loadVisitors}
            loading={visitorsLoading}
          >
            Refresh
          </Button>
        </div>
      </Card>

      {/* Filters */}
      <Card className="shadow-soft border border-border-light rounded-xl">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Search
              placeholder="Search visitors..."
              allowClear
              enterButton={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filter by status"
              allowClear
              style={{ width: '100%' }}
              value={statusFilter}
              onChange={setStatusFilter}
            >
              <Select.Option value="all">All Visitors</Select.Option>
              <Select.Option value="normal">Normal (0-75%)</Select.Option>
              <Select.Option value="warning">Warning (75-90%)</Select.Option>
              <Select.Option value="critical">Critical (90-100%)</Select.Option>
              <Select.Option value="exceeded">Exceeded (100%+)</Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <RangePicker
              placeholder={['Start date', 'End date']}
              style={{ width: '100%' }}
              value={dateRange}
              onChange={setDateRange}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Button
              icon={<ClearOutlined />}
              onClick={() => {
                setSearchText('');
                setStatusFilter('all');
                setDateRange(null);
              }}
              style={{ width: '100%' }}
            >
              Clear Filters
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Quick Stats */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={6}>
          <Card className="text-center">
            <Statistic
              title="Total Visitors"
              value={visitors.length}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card className="text-center">
            <Statistic
              title="Active Today"
              value={visitors.filter(v => {
                const lastActivity = new Date(v.lastActivity);
                const today = new Date();
                return lastActivity.toDateString() === today.toDateString();
              }).length}
              prefix={<ApiOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card className="text-center">
            <Statistic
              title="Near Limit"
              value={visitors.filter(v => {
                const percentage = (v.requestCount / config.visitorRequestLimit) * 100;
                return percentage >= 75 && percentage < 100;
              }).length}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card className="text-center">
            <Statistic
              title="Exceeded"
              value={visitors.filter(v => v.requestCount >= config.visitorRequestLimit).length}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Visitors Table */}
      <Card 
        title="Visitor Details"
        className="shadow-soft border border-border-light rounded-xl"
        extra={
          <Text className="text-sm text-gray-500">
            {filteredVisitors.length} of {visitors.length} visitors
          </Text>
        }
      >
        <Table
          dataSource={filteredVisitors}
          columns={columns}
          loading={visitorsLoading}
          rowKey="_id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: filteredVisitors.length,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} visitors`,
            onChange: (page, pageSize) => setPagination({ current: page, pageSize, total: filteredVisitors.length }),
          }}
          scroll={{ x: 800 }}
        />
      </Card>
    </div>
  );
};

export default VisitorList;